import { Component, inject, ElementRef, Renderer2, computed, effect, untracked, signal, model } from '@angular/core';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { SelfUserStore, eUserSituation, SystemModulesStore, SystemModules } from '@person/user/self/data-access';
import { ModalComponent } from '@shared/components/ui/modal';
import { SystemIconsComponent } from '@shared/components/ui/system-icons';
import { ButtonComponent } from '@shared/components/ui/button';
import { TranslateModule } from '@ngx-translate/core';
import { AlertStore } from '@alert/data-access';
import { RouterStore } from '@route/data-access';
import { AccountStore, AuthStore } from '@account/data-access';

@Component({
  selector: 'lib-modal-expiration',
  imports: [
    CommonModule,
    ModalComponent,
    SystemIconsComponent,
    ButtonComponent,
    TranslateModule,
    NgTemplateOutlet
  ],
  templateUrl: './modal-expiration.component.html',
  styleUrl: './modal-expiration.component.css',
})
export class ModalExpirationComponent {
  public selfUserStore = inject(SelfUserStore);
  public systemModulesStore = inject(SystemModulesStore);
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  public alertStore = inject(AlertStore);
  public routerStore = inject(RouterStore);
  public authStore = inject(AuthStore);
  public accountStore = inject(AccountStore);
  public personDataAsync = computed(() => this.selfUserStore?.personDataAsync());
  
  public situation = computed(() => {
    const hasActuarWebModule = this.systemModulesStore.isModuleAllowed(SystemModules.ActuarWeb);
    return hasActuarWebModule ? eUserSituation.Active : eUserSituation.Blocked;
  });
  public showModal = model(false);
  public isHovering = false;

  public eUserSituation = eUserSituation;

  public modulesChange = effect(() => {
    const modulesLoaded = this.systemModulesStore.loadPersonModulesLoaded();
    const hasActuarWebModule = this.systemModulesStore.isModuleAllowed(SystemModules.ActuarWeb);

    if (modulesLoaded) {
      untracked(() => {
        if (!hasActuarWebModule) {
          this.showModal.set(true);
          this.alertStore.create({
            title: 'COMPONENTS.ALERT.ATTENTION',
            message: 'COMPONENTS.MODAL_EXPIRATION.VERIFYING_SUBSCRIPTION',
            type: 'warning',
          });
          this.loopVerifyLicense();
        } else {
          this.showModal.set(false);
        }
      })
    }
  })

  public loopVerifyLicense(): void {
    setTimeout(() => {
      if (this.situation() === eUserSituation.Blocked) {
        this.systemModulesStore.refreshModules();
      } 
    }, 20000);
  }



  public onBatteryHover(): void {
    this.isHovering = true;
    const batteryIcons = this.elementRef.nativeElement.querySelectorAll('lib-system-icons');

    batteryIcons.forEach((iconElement: HTMLElement) => {
      const svg = iconElement.querySelector('svg');
      if (svg) {
        this.animateBatteryHover(svg);
      }
    });
  }

  private animateBatteryHover(svg: SVGElement) {
    const batteryColor = svg.querySelector('#batteryColor');
    const batteryBolt = svg.querySelector('#batteryBolt');
    const batteryEye1 = svg.querySelector('#batteryEye1');
    const batteryEye2 = svg.querySelector('#batteryEye2');
    const batteryEye3 = svg.querySelector('#batteryEye3');
    const batteryEye4 = svg.querySelector('#batteryEye4');
    const batteryNose1 = svg.querySelector('#batteryNose1');
    const batteryNose2 = svg.querySelector('#batteryNose2');

    if (batteryColor) this.renderer.setStyle(batteryColor, 'fill', '#efb750');
    if (batteryBolt) {
      this.renderer.setStyle(batteryBolt, 'fill', '#efb750');
      this.renderer.setStyle(batteryBolt, 'stroke', '#efb750');
    }
    if (batteryEye1) this.renderer.setStyle(batteryEye1, 'display', 'none');
    if (batteryEye2) this.renderer.setStyle(batteryEye2, 'display', 'none');
    if (batteryEye3) this.renderer.setStyle(batteryEye3, 'display', 'block');
    if (batteryEye4) this.renderer.setStyle(batteryEye4, 'display', 'block');
    if (batteryNose1) this.renderer.setStyle(batteryNose1, 'display', 'none');
    if (batteryNose2) this.renderer.setStyle(batteryNose2, 'display', 'block');
  }

  onBatteryLeave() {
    this.isHovering = false;
    const batteryIcons = this.elementRef.nativeElement.querySelectorAll('lib-system-icons');

    batteryIcons.forEach((iconElement: HTMLElement) => {
      const svg = iconElement.querySelector('svg');
      if (svg) {
        this.animateBatteryLeave(svg);
      }
    });
  }

  private animateBatteryLeave(svg: SVGElement) {
    const batteryColor = svg.querySelector('#batteryColor');
    const batteryBolt = svg.querySelector('#batteryBolt');
    const batteryEye1 = svg.querySelector('#batteryEye1');
    const batteryEye2 = svg.querySelector('#batteryEye2');
    const batteryEye3 = svg.querySelector('#batteryEye3');
    const batteryEye4 = svg.querySelector('#batteryEye4');
    const batteryNose1 = svg.querySelector('#batteryNose1');
    const batteryNose2 = svg.querySelector('#batteryNose2');

    if (batteryColor) this.renderer.setStyle(batteryColor, 'fill', '#e95753');
    if (batteryBolt) {
      this.renderer.setStyle(batteryBolt, 'fill', 'none');
      this.renderer.setStyle(batteryBolt, 'stroke', '#999');
    }
    if (batteryEye1) this.renderer.setStyle(batteryEye1, 'display', 'block');
    if (batteryEye2) this.renderer.setStyle(batteryEye2, 'display', 'block');
    if (batteryEye3) this.renderer.setStyle(batteryEye3, 'display', 'none');
    if (batteryEye4) this.renderer.setStyle(batteryEye4, 'display', 'none');
    if (batteryNose1) this.renderer.setStyle(batteryNose1, 'display', 'block');
    if (batteryNose2) this.renderer.setStyle(batteryNose2, 'display', 'none');
  }

  public viewBilling(): void {
    this.routerStore.navigate(['/my-billing', this.personDataAsync()?.PersonId]);
  }

  public signOut(): void {
    this.showModal.set(false);
    this.authStore.logout();
  }

}
